import * as Sentry from "@sentry/react";
import type { ResolvedComponent } from "@inertiajs/react";
import { createInertiaApp } from "@inertiajs/react";
import { Agentation } from "agentation";
import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";

import { initializeTheme } from "@/hooks/use-appearance";
import PersistentLayout from "@/layouts/persistent-layout";
import { APP_DISPLAY_NAME } from "@/lib/app-display-name";
import { NuqsAdapter } from "@/lib/nuqs-inertia-adapter";
import { initializeProductAnalytics } from "@/lib/product-analytics";

const appName = APP_DISPLAY_NAME;
const agentationEndpoint = import.meta.env.VITE_AGENTATION_ENDPOINT ?? "http://localhost:4747";
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment =
  import.meta.env.VITE_SENTRY_ENVIRONMENT ?? (import.meta.env.DEV ? "development" : "production");

function parseSampleRate(value: string | undefined, fallback: number) {
  if (!value) return fallback;

  const parsedValue = Number.parseFloat(value);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

if (sentryDsn && import.meta.env.PROD) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    sendDefaultPii: true,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    replaysSessionSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      import.meta.env.DEV ? 1.0 : 0.1,
    ),
    replaysOnErrorSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1.0,
    ),
  });
}

initializeProductAnalytics();

const pages = import.meta.glob<{ default: ResolvedComponent }>("../pages/**/*.tsx");

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: async (name) => {
    const path = `../pages/${name}.tsx`;
    const importPage = pages[path];
    if (!importPage) {
      const message = `Missing Inertia page component: '${name}.tsx'`;
      console.error(message);
      throw new Error(message);
    }

    const page = await importPage();

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    page.default.layout ??= [PersistentLayout];

    return page.default;
  },

  setup({ el, App, props }) {
    const app = (
      <StrictMode>
        <NuqsAdapter>
          <>
            <App {...props} />
            {import.meta.env.DEV && <Agentation endpoint={agentationEndpoint} />}
          </>
        </NuqsAdapter>
      </StrictMode>
    );

    if (el.hasChildNodes()) {
      hydrateRoot(el, app);
      return;
    }

    createRoot(el).render(app);
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error;
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    );
  }
});

// This will set light / dark mode on load...
initializeTheme();
